import { useCallback, useEffect, useRef, useState } from 'react'

/** Auto-close so revealed credentials never sit exposed indefinitely. */
const AUTO_CLOSE_MS = 15000

type CardRevealDisclosure = {
  open: boolean
  toggle: () => void
  close: () => void
  /**
   * Put this on the element that wraps BOTH the trigger and the panel — a click
   * outside it (or Escape) closes the reveal, a click inside does not.
   */
  containerRef: React.RefObject<HTMLDivElement>
}

/**
 * Non-modal disclosure state for the card-details reveal, owned by the hero
 * shell so the full hero and the (future) compact hero share one source of
 * truth. It is a disclosure, not a modal: the page stays interactive and focus
 * is not trapped. Three ways close it — toggle off, Escape, an outside click —
 * plus an auto-close timeout so sensitive values are not left on screen.
 */
export const useCardRevealDisclosure = (
  autoCloseMs: number = AUTO_CLOSE_MS,
): CardRevealDisclosure => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Updaters stay pure — React double-invokes them in StrictMode dev and may
  // replay them when a concurrent render is discarded, so no timer bookkeeping
  // lives here. The timeout is armed declaratively by the effect below.
  const close = useCallback((): void => setOpen(false), [])
  const toggle = useCallback((): void => setOpen((current) => !current), [])

  // Auto-close: while open, arm a single timeout that closes the panel so
  // revealed credentials never sit exposed indefinitely. Keyed on `open`, so
  // reopening re-arms a fresh timer and every close path (toggle, Escape,
  // outside click, unmount) clears it through this effect's cleanup.
  useEffect(() => {
    if (!open) return undefined
    const timer = setTimeout(() => setOpen(false), autoCloseMs)
    return () => clearTimeout(timer)
  }, [open, autoCloseMs])

  // Escape + outside-click dismissal, attached only while open. The listener is
  // added after the opening click has finished propagating, so it never
  // self-closes on the same click that opened it.
  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close()
    }
    const onDocumentClick = (event: MouseEvent): void => {
      const target = event.target as Node
      if (containerRef.current && !containerRef.current.contains(target))
        close()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onDocumentClick)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onDocumentClick)
    }
  }, [open, close])

  return { open, toggle, close, containerRef }
}
